import { httpClient } from '@/shared/api/httpClient'

import {
  nativeRefreshBody,
  persistRotatedRefresh,
  refreshTokenStore,
} from '../lib/refreshTokenStore'
import type {
  AuthResponse,
  CurrentUser,
  EmailVerificationConfirmResponse,
  LoginPayload,
  LoginResponse,
  RefreshResponse,
  RegisterPayload,
  RegisterResponse,
  TwoFactorEnrollResponse,
} from '../types/auth.types'

export const authApi = {
  register: (body: RegisterPayload) =>
    httpClient.post<RegisterResponse>('/auth/register', body).then((r) => r.data),

  login: (body: LoginPayload) =>
    httpClient.post<LoginResponse>('/auth/login', body).then((r) => r.data),

  twoFactorEnroll: (challengeToken: string) =>
    httpClient
      .post<TwoFactorEnrollResponse>('/auth/2fa/enroll', undefined, {
        headers: { Authorization: `Bearer ${challengeToken}` },
      })
      .then((r) => r.data),

  twoFactorEnrollConfirm: (challengeToken: string, code: string) =>
    httpClient
      .post<AuthResponse>(
        '/auth/2fa/enroll/confirm',
        { code },
        { headers: { Authorization: `Bearer ${challengeToken}` } },
      )
      .then((r) => r.data),

  twoFactorVerify: (challengeToken: string, code: string) =>
    httpClient
      .post<AuthResponse>(
        '/auth/2fa/verify',
        { code },
        { headers: { Authorization: `Bearer ${challengeToken}` } },
      )
      .then((r) => r.data),

  refresh: async () => {
    // Nativo: manda el refresh token guardado en el body y persiste el rotado.
    // Web: body undefined → rota por cookie httpOnly (sin cambios).
    const body = await nativeRefreshBody()
    const { data } = await httpClient.post<RefreshResponse>('/auth/refresh', body)
    await persistRotatedRefresh(data.refreshToken)
    return data
  },

  logout: async () => {
    const body = await nativeRefreshBody()
    try {
      await httpClient.post<void>('/auth/logout', body)
    } finally {
      // Aunque falle el POST, en nativo borramos el token local (sesión cerrada).
      await refreshTokenStore.clear()
    }
  },

  me: () => httpClient.get<CurrentUser>('/auth/me').then((r) => r.data),

  requestPasswordReset: (email: string) =>
    httpClient.post<void>('/auth/password-reset/request', { email }).then(() => undefined),

  confirmPasswordReset: (token: string, newPassword: string) =>
    httpClient
      .post<void>('/auth/password-reset/confirm', { token, newPassword })
      .then(() => undefined),

  confirmEmailVerification: (token: string) =>
    httpClient
      .post<EmailVerificationConfirmResponse>('/auth/email-verification/confirm', { token })
      .then((r) => r.data),

  requestEmailVerification: (email: string) =>
    httpClient.post<void>('/auth/email-verification/request', { email }).then(() => undefined),
}
